//
// Mental Canvas web viewer.
// (c) 2016-2020 Mental Canvas Inc.
//
// From ES5 - execute MentalCanvas.default.run(options) to start scene loading.
// From ES6 - import DefaultExport and call run(options) to start scene loading.
//

import {
    BufferGeometry,
    Clock,
    Color,
    EventDispatcher,
    Frustum,
    Group,
    LoadingManager,
    Material,
    Matrix4,
    Mesh,
    MeshPhongMaterial,
    Object3D,
    PerspectiveCamera,
    Plane,
    Quaternion,
    Scene,
    ShaderMaterial,
    Sphere,
    Texture,
    Vector2,
    Vector3,
    WebGLRenderer,
    WebGLRenderTarget,
} from 'three';

// Stupid enums
import {
    ClampToEdgeWrapping,
    DoubleSide,
    LinearFilter,
    LinearMipMapLinearFilter,
    NoToneMapping
} from 'three';
import {
    MaterialCreator,
    MaterialInfo,
    MTLLoader
} from './libs/MTLLoader';
import { OBJLoader } from './node_modules/three/examples/jsm/loaders/OBJLoader';
import * as $ from 'jquery';

import {
    _Math,
    CameraUtils,
    MergeSpheres,
    Ray,
    RBT
} from './utils';
import { BSP } from './bsp';
import { Bookmark } from './MCBookmark';
import { MCOptions } from './MCOptions';
import {
    CameraAnimator,
    Transition
} from './MCCameraAnimator';
import {
    ControlLimits,
    MCControls
} from './MCControls';
import {
    CameraConstraints,
    PlaneConstraint
} from './MCCameraConstraints';
import {
    throttle
} from './libs/limit';


// @ts-ignore
Function.prototype.throttle = throttle;

// iPadOS13... or, an older iOS with "request desktop site" activated. In both cases,
// the useragent string will look 100% like MacOS.
const bIPadOS13 : boolean = navigator.platform=='MacIntel' && navigator.maxTouchPoints>1;

class MCShaderMaterial extends ShaderMaterial {
    m_bLoaded : boolean = false;
    m_loadOpacity : number = 0.0;

    dispose() {
        super.dispose();
        // Assume no texture sharing.
        let texture = <Texture>this.uniforms.texture0.value;
        texture.dispose();
        texture.image = null;
    }
};

const MCErrorCodeKeys = {
    'none': 0,
    'graphicsStartupFailed': 0,
    'downloadFailed': 0,

    // Warnings
    'oldDevice': 0
};
export type MCErrorCode = keyof typeof MCErrorCodeKeys;
export class MCError extends Error
{
    constructor(code: MCErrorCode, message?: string) {
        super(message);
        this.type = code;
        this.isWarning = (code == 'oldDevice');
    }
    type: MCErrorCode;
    isWarning: boolean;
}
const MCModeKeys = {
    'paused': 0,
    'playing': 0
};
export type MCMode = keyof typeof MCModeKeys;
function toggleMode(mode: MCMode): MCMode
{
    return mode == 'playing' ? 'paused' : 'playing';
}

class CanvasTexture extends Mesh {
    m_boundingSphere: Sphere = null;
    m_canvasName: string;
    dispose() {
        //super.dispose();
    }
    isCanvasTexture() {
        return true;
    }
    // Sort of mixinish constructor.
    static FromMesh(mesh: Mesh): CanvasTexture
    {
        var ct = <CanvasTexture>mesh;
        if (!mesh.hasOwnProperty('isCanvasTexture')) {
            ct.m_boundingSphere = null;
            ct.m_canvasName = undefined;
            ct.dispose = CanvasTexture.prototype.dispose;        
            ct.isCanvasTexture = CanvasTexture.prototype.isCanvasTexture;
        }
        return ct;
    }
};
class Canvas {
    m_cts: CanvasTexture[] = [];
    m_plane: Plane = null;
    m_linkUrl: string;
    m_visToggleCanvasName: string;
    m_bPulse: boolean;
    dispose() {
        for (let ct of this.m_cts)
            ct.dispose();
        this.m_cts = [];
    }
};
class OpacityAnimation {
    startTime: number;
    endTime: number;
    startOpacity: number;
    endOpacity: number;

    constructor( startTime: number, endTime: number, startOpacity: number, endOpacity: number ) {
        this.startTime = startTime;
        this.endTime = endTime;
        this.startOpacity = startOpacity;
        this.endOpacity = endOpacity;    
    }
    dispose() {}
}
export class InteractionData {
    constructor(scene: MCScene)
    {
        this.rbt = CameraUtils.GetRBT(scene.camera);
        this.scrubT = scene.cameraAnimator.GetElapsedTimeNormalized();
        this.isOnPath = scene.cameraAnimator.IsOnBookmarkPath();
    }

    rbt: RBT;
    scrubT: number;
    isOnPath: boolean;
    serializeToURLSearch(urlSearch: string)
    {
        var searchTerms : string[] = urlSearch ? urlSearch.slice(1).split('&') : [];
        var bFoundCamera = false;
        var bFoundScrub = false;
        var bFoundOnPath = false;
        for (let i = 0; i < searchTerms.length; ++i) {
            if (searchTerms[i].match(/^camera=/i)) {
                searchTerms[i] = 'camera=' + this.rbt.toString();
                bFoundCamera = true;
            } else if (searchTerms[i].match(/^scrub=/i)) {
                searchTerms[i] = 'scrub=' + this.scrubT.toFixed(3);
                bFoundScrub = true;
            } else if (searchTerms[i].match(/^onPath=/i)) {
                searchTerms[i] = 'onPath=' + this.isOnPath.toString();
                bFoundOnPath = true;
            }
        }
        if (!bFoundCamera)
            searchTerms.push('camera='+ this.rbt.toString());
        if (!bFoundScrub)
            searchTerms.push('scrub='+ this.scrubT.toFixed(3));
        if (!bFoundOnPath)
            searchTerms.push('onPath='+ this.isOnPath.toString());
        return '?' + searchTerms.join('&');
    }
};

// FOR COLLAB SESSIONS - override a few aspects of MTLLoader behavior.
//
// This hasn't been tested since it was refactored in Feb. 2019
// This is obviously tightly coupled with the original implementation of MTLLoader.parse()
class MCMTLLoader extends MTLLoader {
    materialCreator : MaterialCreator = null;
    materialMap: {[key: string]: string} = {};
    constructor(manager: LoadingManager)
    {
        super(manager);
    }
    dispose() {
        //super.dispose();
        this.materialCreator = null;
        this.materialMap = {};
    }
    // @ts-ignore - MTLLoader.d.ts hasn't been updated for signature change
    parse(text: string, path: any): MaterialCreator {
        // @ts-ignore - MTLLoader.d.ts hasn't been updated for signature change
        var newMaterialCreator = super.parse(text, path);
        var loader = this;
        newMaterialCreator.callbackDiffuseTexture = function ( materialName:string, textureName: string ) {
            loader.materialMap[textureName] = materialName;
        };
        if (this.materialCreator === null) {
            this.materialCreator = newMaterialCreator;
        } else {
            // Share the materialcreator across calls, so we can reuse materials. Retain previous materials that are
            // still in use, discard unused, add any ones that have new, distinct names

            // Discard the newly created materialcreator, but add its materialsInfo to our existing creator.
            this.addMaterials(newMaterialCreator.materialsInfo);
        }
        return this.materialCreator;
    }
    addMaterials(convertedMaterialsInfo: {[key: string]: MaterialInfo}) {
        var newMaterialsInfo : {[key: string]: MaterialInfo} = {};
        var retained : {[key: string]: boolean} = {};
        var oldMaterialsInfo = this.materialCreator.materialsInfo;
        for (var mn in convertedMaterialsInfo)
            if (oldMaterialsInfo[mn] === undefined)
                newMaterialsInfo[mn] = convertedMaterialsInfo[mn];
            else {
                newMaterialsInfo[mn] = oldMaterialsInfo[mn];
                retained[mn] = true;
            }
        for (var mn in oldMaterialsInfo)
            if (!retained.hasOwnProperty(mn)) {
                // @ts-ignore:2339 - disposeFlag is a flag that MentalCanvas manually added to the material,
                // in our customized MTLLoader code.
                this.materialCreator.materials[mn].disposeFlag = true;
                // Removes our reference to the material, but will still be alive (mesh also refers to it)
                delete this.materialCreator.materials[mn];
                delete this.materialCreator.nameLookup[mn];
            }

        this.materialCreator.materialsInfo = newMaterialsInfo;
        // Ignore any duplicates. Older item takes precedence.
    }
};

export class MCScene extends EventDispatcher {
    protected options: MCOptions;
    // Three
    protected renderer: WebGLRenderer;
    camera: PerspectiveCamera;
    protected scene: Scene;
    private clock: Clock;
    // Mostly obsolete - replaced by m_canvases.m_cts. Each is one CanvasTexture.
    private arrayofobjects: Mesh[] = [];
    protected mtlLoader: MCMTLLoader;

    // Ours
    controls: MCControls;
    cameraAnimator: CameraAnimator;
    private bsp: BSP = null;

    private stages: ControlLimits[] = [];
    private bookmarks: Bookmark[] = [];

    // N.B. introduced in authoring 12870. In older scenes, there will be one "canvas"
    // for each tile of an authoring canvas.
    private m_canvases: {[canvasName: string]: Canvas} = {};

    private opacityAnimations : {[key: string]: OpacityAnimation} = {};

    // Hyperlink data
    private bHasPulseCanvases: boolean = false;
    private pulseTime: number = 0.0; // True if the scene contains at least one 'pulse' canvas.
    private lastPulseRedrawTime: number = -1.0;

    private sceneAspectRatio: number = 1.6;
    // Vertical field-of-view when device is in portrait orientation.
    private fovvPortrait: number = 80;
    private stageId: number = 0;
    protected mode: MCMode = 'paused';
    private TexturesLoaded: boolean = false;
    private RedrawRequested: boolean = false; // Set this to true to request a redraw next anim tick.
    private m_bRenderSuppressed: boolean = false;
    private linkVizMode: boolean = false;
    private smallnessEstimate = 1.0;

    protected reloadFlag: boolean = false;
    // Only used during loading
    private interimCenter: Vector3;
    protected incomplete: any;
    protected filenameObj: string;

    private mcxVersion: number;

    // Collab settings
    protected bIsCollabSession: boolean = false;
    protected loadErrorFlag: boolean = false;
    protected needsResetCamera: boolean = false;


    private scrubbing: boolean = false;

    // Picking.
    private bIsPickEnqueued: boolean = false;
    private pickClientX: number = 0;
    private pickClientY: number = 0;
    private pickTarget : WebGLRenderTarget;
    private pickBuffer : Uint8Array;

    constructor(container: HTMLDivElement, options: MCOptions, failWarnHandler: (error: MCError) => void)
    {
        super();

        //console.time( 'ctor' );

        this.options = options.clone();

        ////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // Set up a canvas that supports WebGL2
        let canvasElem : HTMLCanvasElement = document.createElement('canvas');
        container.appendChild(canvasElem);
        let contextWebGL2: WebGL2RenderingContext = canvasElem.getContext( 'webgl2', { depth: false, alpha: false, antialias: false } );
        try {
            this.renderer = new WebGLRenderer({
                depth: false,
                stencil: false,
                canvas: canvasElem,
                context: contextWebGL2,
                //powerPreference: 'high-performance'
            });
        } catch(errIn) {
            const err: Error = <Error>errIn;
            failWarnHandler(new MCError('graphicsStartupFailed', err.message));
            return;
        }
        console.log( 'isWebGL2 ' + this.renderer.capabilities.isWebGL2 );

        ////////////////////////////////////////////////////////////////////////////////////////////////////////////
        // When using texture size below "low", this will override WebGL's actual maximum texture size, in order
        // to trigger a software downsampling of incoming textures in THREE's uploadTexture function.
        if (this.options.maxImageSize !== undefined)
            this.renderer.capabilities.maxTextureSize = Math.min(this.renderer.capabilities.maxTextureSize, options.maxImageSize);
        this.renderer.toneMapping = NoToneMapping;
        this.renderer.setPixelRatio(window.devicePixelRatio);

        // If non-fullscreen, both of these will be updated on the first actual render.
        if (this.options.fullscreen) {
            container.style.position = 'absolute';
            container.style.left = container.style.top = '0px';
            container.style.width = container.style.height = '100%';
            // This will also resize the associated DOM element.
            this.renderer.setSize(window.innerWidth, window.innerHeight);
        }
        this.camera = new PerspectiveCamera(60, window.innerWidth / window.innerHeight, .1, 1000);
        
        this.controls = new MCControls(this, this.camera, this.renderer.domElement);
        // if (this.options.limits == null) {
        //     this.options.limits = new ControlLimits;
        // }
        if (this.options.controlsMode === 'disableRotate')
            this.controls.enableRotate = false;
        else if (this.options.controlsMode === 'authoringNavMode') {
            this.controls.enableAuthoringNavMode = true;
            this.options.limits.Pan = false; // ignore any provided panlimits
        }
        else if (this.options.controlsMode === 'egocentric') {
            this.controls.enableAuthoringNavMode = true;
            this.controls.arcball.bEgocentric = true;
            if (this.options.limits != null)
                this.options.limits.Pan = false;
        }
        this.controls.addEventListener('cameramove', this.onControlsCameraMove);
        this.controls.addEventListener('interactioncomplete', this.onControlsInteractionComplete);
        this.controls.addEventListener('updateActiveBookmark', () => this.dispatchEvent({type: 'movementEnd'}));

        this.scene = new Scene;
        this.clock = new Clock;
        this.cameraAnimator = new CameraAnimator();

        this.pickTarget = new WebGLRenderTarget( 1, 1, {
            depthBuffer: false,
            stencilBuffer: false,
            generateMipmaps: false
        } );
        this.pickBuffer = new Uint8Array(4);

        // We ideally don't want to receive resize events until loading is complete... but also don't want to miss events.
        if (this.options.fullscreen)
            window.addEventListener('resize', this.onWindowResize, false);

        // TODO: move this logic to MCOptions at some point.
        let url : string;
        if (this.options.path.match(/.mcx$/)) {
            url = this.options.path;
            this.options.path = this.options.path.replace(/\/[^/]*$/, '');
        } else {
            url = this.options.path + '/Scene' + this.getCollabFrameSuffix() + '.mcx';
        }
        var sceneReq = $.ajax({ type: 'GET', url: url, xhrFields: {}, });
        sceneReq.done(this.onLoadMCX);
        sceneReq.fail((jqxhr, textStatus: JQuery.Ajax.TextStatus, errorThrown: string) => {
            failWarnHandler(new MCError('downloadFailed', url + '\n' + errorThrown));
        });
        //console.timeEnd( 'ctor' );
    }
    addThrottledEventListener(type: string, interval: number, listener: (event: Event) => void) {
        // @ts-ignore
        this.addEventListener(type, listener.throttle(interval));
    }
    protected getCollabFrameSuffix() {
        return '';
    }
    protected onControlsCameraMove = (event) =>
    {
        this.RedrawRequested = true;
        // this.syncControlsWithBookmark();
        this.dispatchEvent({type: 'movementEnd'});
    }
    private onControlsInteractionComplete = (event) =>
    {
        this.dispatchEvent({type: 'interactioncomplete', attachment: new InteractionData(this)});
        // this.syncControlsWithBookmark();
    }
    private setSceneInfoAndBookmarks(json: any) {
        // Parse the JSON properties that once used to be in ScenePreferences.json.
        this.renderer.setClearColor(json.BackgroundColor || 0xffffff);
        // document.getElementById("")
        if (json.Title && json.Title != '[Not Set]')
            document.title = json.Title;

        // The WebViewer implementation of Z fade is *slightly* different than authoring, but should give the same results.
        // - Authoring: camera's specified near plane is *centre* point of fade, and at render time it's modified so the
        //   actual near clip is (near-sc/2) and the far fade point is (near+sc/2).
        // - WebViewer: camera's specified near plane is the *start* point of fade, allowing us to avoid modifying the
        //   camera at render time.
        // 
        // For near=0.1 and sc=0.05, we get fade between z=0.075 ... 0.125
        //
        // Adjust the 0.1 near plane (centre of fade) to the start-of-fade point.
        this.camera.near = 0.1 * 0.75;
        if (json.hasOwnProperty('smallnessEstimate')) {
            this.smallnessEstimate = <number>json.smallnessEstimate;
            this.camera.near /= this.smallnessEstimate;
        }

        var stage_default : ControlLimits = new ControlLimits;
        if (this.mcxVersion >= 3)
            // Everything's already exported in a cleanly fenced off subobject, exactly
            // matching names and types of Javascript version.
            // Some fields may be missing though - so we won't just cast json.Limits to a ControlLimits object.
            // Just override default object with any fields coming from json.Limits.
            Object.assign(stage_default, json.Limits);
        else {
            stage_default = new ControlLimits;
            for (var key in json)
                if (key.match('^Limits\\.'))
                    // Older MCX files: all values are string-typed, have to be parsed to
                    // yield the correct types.
                    stage_default.parseString(key.replace('Limits.', ''), json[key]);
        }
        this.bookmarks = [];
        var aspectList = [];
        for (var i = 0; i < json.bookmarks.length; i++) {
            const b = json.bookmarks[i];
            if (b.camera.aspect)
                aspectList.push(b.camera.aspect);
            var cor = b.centerOfRotation;
            var bookmark = new Bookmark(
                new Quaternion(b.camera.qx, b.camera.qy, b.camera.qz, b.camera.qw),
                new Vector3(b.camera.x, b.camera.y, b.camera.z),
                b.IsInAnim,
                b.linger,
                b.transition);
            bookmark.aspect = b.camera.aspect;
            bookmark.stageId = b.stage || 0;
            bookmark.centerOfRotation = cor ? new Vector3(cor.x, cor.y, cor.z) : null;
            bookmark.hiddenTileNames = (b.hiddenTileNames != null) ? b.hiddenTileNames : [];
            this.bookmarks.push(bookmark);
        }
        if (json.hasOwnProperty('aspectRatio'))
            this.sceneAspectRatio = <number>json.aspectRatio;
        else {
            aspectList.sort();
            if (aspectList.length > 0)
                this.sceneAspectRatio = aspectList[Math.floor(aspectList.length / 2)];
        }
        // Added very slightly after mcxVersion 2.
        if (json.hasOwnProperty('fovvPortrait'))
            this.fovvPortrait = <number>json.fovvPortrait;
        // Older files: just leave member at 60, use an older logic for deciding
        // portrait behavior

        // "Stage": info about controls settings (angle limits, center of rotation)
        // that is shared across multiple bookmarks. (e.g., bookmarks #1-5 are stage #1,
        // bookmarks #6-10 are stage #2, etc.)  Must be manually entered into MCX
        // file; not exported from Authoring.
        //
        // Stages concept is nearing the end of its life... but keep it alive for now.
        // Per-bookmark data is probably the way forward. When stages=null, we generate
        // as set of pseudo-stages, each corresponding to a single bookmark.
        this.stages = [];
        var stage: ControlLimits;
        // If limits we provided as an option, they take precedence.
        if (this.options.limits != null)
            stage_default = this.options.limits.clone();
        if (json.stages == null) {
            // console.log('no stages were specified.');
            // Add one stage per bookmark if no stages were specified.
            for (i = 0; i < this.bookmarks.length; i++) {
                stage = stage_default.clone();
                stage.center = this.bookmarks[i].centerOfRotation;  // May be null
                this.stages.push(stage);
                this.bookmarks[i].stageId = i;
            }
            //console.log('default azimuthRange: ', stages[0].AzimuthRange);
        }
        else {
            // console.log('founds stages; ', json.stages.length, ' were specified.');
            for (i = 0; i < json.stages.length; i++) {
                stage = stage_default.clone();
                for (key in json.stages[i])
                    if (key.match('^Limits\\.'))
                        stage[key.replace('Limits.', '')] = json.stages[i][key];
        
                // Special for cosmo: if we have 1:1, borrow bookmarks centres.
                if (json.stages.length == this.bookmarks.length) {
                    this.bookmarks[i].stageId = i;
                    stage.center = this.bookmarks[i].centerOfRotation;
                }
                this.stages.push(stage);
            }
        }

        // load bookmarks into animator
        this.cameraAnimator.SetupAnimation_Bookmarks(this.mtlLoader.materialCreator.materials, this.bookmarks, false);
        
        // store the bool that says whether we need to reset the camera to bookmark 0 (e.g. scene change). we will reset camera later on if this is true.
        // Always reset camera for non-collab sessions.
        this.needsResetCamera = !this.bIsCollabSession || json.resetCamera;
        this.linkVizMode = json.linkVizMode;

        if (this.options.scrub >= 0) {
            // scrub position was specified in url
            this.needsResetCamera = false;
            // this also sets camera and visibilities
            this.setAnimationScrubT(this.options.scrub, false);
        }

        this.cameraAnimator.SetOnBookmarkPath(true);

        if (this.options.camera) {
            this.needsResetCamera = false;
            this.camera.position.copy(this.options.camera.translation);
            this.camera.quaternion.copy(this.options.camera.rotation);
            if (!this.options.onPath)
                // if we are specifying camera position and not onPath, then we have to assume off path
                // also, we can only allow being off path if we know the camera position
                this.cameraAnimator.SetOnBookmarkPath(false);
            if (this.options.vizMode == 'positionBased' && !this.cameraAnimator.IsOnBookmarkPath() && this.linkVizMode)
                this.setTileOpacitiesBasedOnCameraPosition();
            else if (this.options.scrub < 0)
                // we have a specified camera but no scrub position. we have to assume bookmark 0 opacities
                this.setTileOpacitiesBasedOnBookmark(0);
            this.syncControlsWithBookmark();
        }
    }
    dispose() {
        if (!this.TexturesLoaded)
            console.error('Dispose before load complete. Wait for loadComplete event before disposing.');

        // Prevent any further attempts to schedule an animation.
        window.cancelAnimationFrame(this.m_animationFrameHandle);

        this.m_animationFrameHandle = undefined;
        this.controls.dispose();
        this.controls = null;
        this.cameraAnimator.dispose();
        this.cameraAnimator = null;
        for (let key in this.m_canvases)
            this.m_canvases[key].dispose();
        this.m_canvases = {};
        for (let key in this.opacityAnimations)
           this.opacityAnimations[key].dispose();
        this.opacityAnimations = null;

        this.bsp.dispose();
        this.bsp = null;
        // Double-reference of each Mesh - they are also children of this.scene.
        // TODO: kill it...!
        this.arrayofobjects = null;
        // Double-reference of each Material - they are also part of the scene.
        // TODO: kill it...!
        this.mtlLoader = null;

        for (let obj of this.scene.children) {
            let mesh = <CanvasTexture>obj;
            (<BufferGeometry>mesh.geometry).dispose();
            (<MCShaderMaterial>mesh.material).dispose();
        }
        // Scene dispose... does nothing. Gr.
        this.scene.dispose();
        this.scene = null;
        this.pickTarget.dispose();
        this.pickTarget = null;
        this.renderer.dispose();
        //this.renderer.domElement.style.opacity = '20%';
        this.renderer.domElement.remove();
        this.renderer = null;
    }
    protected resetCamera() {
        if (this.bookmarks.length == 0)
            console.error('Scene has no bookmarks.');
        this.setCameraToBookmark(0);
    }
    private onLoadMCX = (response: XMLHttpRequestResponseType) => {
        //console.time( 'onLoadMCX' );
        var json = JSON.parse(response);

        this.mcxVersion = json.hasOwnProperty('mcxVersion') ? json.mcxVersion : 0;
        // Set bIsCollabSession *before* parsing bookmarks, so that we can retain scene limits when in a non-collab session.
        this.bIsCollabSession = json.bIsCollabSession != null ? json.bIsCollabSession : false;
        if (this.bIsCollabSession) {
            console.log('collab session detected');
            this.options.quality = 'high'; // Force quality to high if in a collab session.
        }
        this.controls.arcball.bIsCollabSession = this.bIsCollabSession;

        this.incomplete = {json: json};
        let onLoad = () => {
            this.onSceneCompletelyLoaded();
        };
        let onProgress = (url: string, itemsLoaded: number, itemsTotal: number) => {
            this.RedrawRequested = true; // Request a redraw next anim tick.
            this.dispatchEvent({type:'downloadProgress', attachment:{itemsLoaded: itemsLoaded, itemsTotal: itemsTotal}});
            //console.log('Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.');

            // Efficiently strip path from the url (taking into account both conventions for file separators)
            const textureName : string = url.split('\\').pop().split('/').pop();
            // Won't be true for non-texture URLs.
            if (this.mtlLoader.materialMap.hasOwnProperty(textureName)) {
                const materialName : string = this.mtlLoader.materialMap[textureName];
                if (this.mtlLoader.materialCreator.materials.hasOwnProperty(materialName))
                    (<MCShaderMaterial>this.mtlLoader.materialCreator.materials[materialName]).m_bLoaded = true;
            }
        };
        let onError = (url: string) => {
            console.log('Error! Could not load this file: ' + url);
            this.loadErrorFlag = true;
        }
        this.mtlLoader = new MCMTLLoader(new LoadingManager(onLoad, onProgress, onError));
        this.mtlLoader.setMaterialOptions({
            wrap: ClampToEdgeWrapping
        });
        this.mtlLoader.setPath(this.options.path + '/');
        this.mtlLoader.setResourcePath(this.options.path + '/' + this.options.quality + '/');
        this.mtlLoader.setCrossOrigin('anonymous');
        this.reloadFlag = false;
        this.filenameObj = json.filenameObj || 'Scene.obj'; // Default: only needed for older export files
        this.mtlLoader.load(json.filenameMtl || 'canvas_materials.txt', this.onLoadMaterials);
        //console.timeEnd( 'onLoadMCX' );
    }
    protected onSceneCompletelyLoaded() {
        console.log('Loading complete!');
        this.TexturesLoaded = true;
        this.RedrawRequested = true; // Request a redraw next anim tick.
        this.dispatchEvent({type: 'loadComplete'});
    }
    
    enqueuePick(clientX: number, clientY: number) {
        this.bIsPickEnqueued = true;
        this.RedrawRequested = true;
        const rect = this.renderer.domElement.getBoundingClientRect();
        this.pickClientX = clientX - rect.left;
        this.pickClientY = clientY - rect.top;
        console.log('enqueuePick: ' + this.pickClientX + ' ' + this.pickClientY + '(' + clientX + ' ' + clientY + ')' );
    }

    protected onLoadMaterials = (creator: MaterialCreator) => {
        //console.time('onLoadMaterials');
        creator.preload();
        var anisotropicRatio = this.incomplete.json.anisotropy || 4;
        anisotropicRatio = Math.min(anisotropicRatio, this.renderer.capabilities.getMaxAnisotropy());

        for (var matName in creator.materials) {
            // Just retrieve the texture map from the auto-generated material. Throw the rest out,
            // and replace it with our own MCShaderMaterial.
            var map = (<MeshPhongMaterial>creator.materials[matName]).map;
            // New fragment shader supporting near-plane fade! Only uses diffuse texture map from OBJ.
            // Note different specification from Authoring. See mcsrc.js where camera.near is set for
            // comments discussing the reasoning.
            var mat = new MCShaderMaterial( {
                uniforms: {
                    zNear: { value: 0.1 },
                    texture0: { type: "t", value: map },
                    opacity: { value: 1.0 },
                    usePickID: { value: 0.0 },
                    pickID_r: { value: 0.0 }, // To be filled in later with CanvasID+1
                    pickID_g: { value: 0.0 },
                    pickID_b: { value: 0.0 }
                },
                vertexShader:
                   `varying vec2 vTexCoord;
                    void main()
                    {
                        vTexCoord = uv;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }`,
                fragmentShader:
                   `uniform sampler2D texture0;
                    uniform float zNear, opacity, usePickID;
                    uniform float pickID_r, pickID_g, pickID_b;
                    varying vec2 vTexCoord;

                    void main() {
                        vec4 c = texture2D(texture0, vTexCoord);
                        float zAlpha = smoothstep(zNear, zNear * 1.66667, 1.0 / gl_FragCoord.w);
                        vec4 result = zAlpha * opacity * c;
                        if ( usePickID < 0.5 ) {
                            // Helpful if we have an invalid premultiplied alpha file, due to
                            // user editing. e.g., (R,G,B,A)=(1.0,1.0,1.0, 0)
                            if (result.a < 0.01)
                                discard;
                        }
                        else
                        {
                            if (result.a <= 0.1)
                                discard;
                            result = vec4( pickID_r, pickID_g, pickID_b, 1.0);
                        }
                        gl_FragColor = result;
                    }`
            });
            mat.lights = false;
            mat.transparent = true;
            mat.side = DoubleSide;
            mat.depthWrite = false;
            mat.depthTest = false;
            mat.alphaTest = 0;
            if (map) {
                map.anisotropy = anisotropicRatio;
                map.magFilter = LinearFilter;   // These are just the defaults, but still
                map.minFilter = LinearMipMapLinearFilter;
                map.generateMipmaps = true;
            }
            mat.premultipliedAlpha = true;
            creator.materials[matName] = mat;
        }
        let objLoader = new OBJLoader(this.mtlLoader.manager);
        // @ts-ignore:2345 - mismatch between our custom MaterialCreator and stock MaterialCreator.
        objLoader.setMaterials(creator);
        objLoader.setPath(this.options.path + '/');

        // All objects will be static - don't reupload matrices every frame.
        Object3D.DefaultMatrixAutoUpdate = false;

        var onProgress = function (event: ProgressEvent) {
            if (event.lengthComputable) {
                var percentComplete: number = event.loaded / event.total * 100;
                console.log(percentComplete.toFixed(2) + '% downloaded');
            }
        };
        objLoader.load(this.filenameObj, this.onLoadObj.bind(this), this.reloadFlag ? undefined : onProgress);
        this.filenameObj = null; // Not needed any more.
        //console.timeEnd('onLoadMaterials');
    }
    protected onLoadObj(object: Group) {
        //console.time( 'onLoadObj' );
        var total_vertices = 0.0;
        this.interimCenter = new Vector3(0, 0, 0);
        for (let childObj3d of object.children) {
            var childMesh = <Mesh>childObj3d;
            var arr = (<BufferGeometry>childMesh.geometry).getAttribute('position').array;
            for (let j = 0; j < arr.length / 3; j++)
                this.interimCenter.add(new Vector3(arr[j * 3], arr[j * 3 + 1], arr[j * 3 + 2]));
            total_vertices += arr.length / 3;
        }
        if (total_vertices > 0.0)
            this.interimCenter.divideScalar(total_vertices);

        this.incomplete.arrayofobjects = [];
        var num_objects = object.children.length;
        for (var i = 0; i < num_objects; i++) {
            object.children[0].position.set(0, 0, 0);
            this.incomplete.arrayofobjects.push(object.children[0]);
            object.children.shift();
        }
        //console.timeEnd( 'onLoadObj' );
        if (this.reloadFlag) {
            console.timeEnd('reload');
            // Don't complete reload until textures are all available.
        }
        else {
            this.activatePendingScene();

            // check if we need to reset the camera to bookmark 0 (e.g. scene change)
            if (this.needsResetCamera)
                this.resetCamera();
            
            // make sure we show that we are paused
            this.mode = 'paused';
            this.dispatchEvent({ type: 'playPause', attachment:this.mode })

            if (!this.bIsCollabSession)
                this.kickoffRenderLoop();
        }
    }
    protected kickoffRenderLoop()
    {
        //console.time( 'kickoffRenderLoop' );

        this.dispatchEvent({type:'startRender', attachment:{started: true}});

        if (this.options.fullscreen)
            this.onResize(true);
        // Non-fullscreen: first render call will update size.

        this.animate();
        //console.timeEnd( 'kickoffRenderLoop' );
    }

    protected activatePendingScene() {
        //console.time( 'activatePendingScene' );
        var bsp = new BSP(this.incomplete.json.bsp, this.incomplete.json.bspCutlet, this.mcxVersion, this.incomplete.arrayofobjects);

        // ---- 8< ------------------------------------
        // For reloads - previous scene is 100% valid and consistent up to here. After this, we are in
        // an intermediate state with neither old nor new scene valid.
        // TODO: pause rendering while messing around with scene
        // ... but I think Javascript scheduling guarantees that.

        if ( this.bIsCollabSession ) {
            while (this.scene.children.length > 0) {
                var obj = <Mesh>this.scene.children[0];
                this.scene.remove(obj);
                var material = <MCShaderMaterial>obj.material;
                // Material loader will set this flag if a material is no longer in use in the
                // incoming scene.
                
                // @ts-ignore:2339 - disposeFlag is a flag that MentalCanvas manually adds to the material,
                // in our customized MTLLoader code.
                if (material.disposeFlag) {
                    // TODO: support other types of materials, not just diffuse texture maps
                    (<Texture>material.uniforms.texture0.value).dispose();
                    material.dispose();
                }
                obj.geometry.dispose();
            }
        }

        var canvasNameByMaterialUUID : {[uuid: number]:string} = {};
        // Grab the canvas list.
        if ( this.incomplete.json.canvasTiles != null ) {
            class CanvasTileList {
                canvas: string;
                tiles: string[];
            };

            // Unfortunately, CanvasTiles is specified as materialnames. Scan through everything to
            // instead build a proper dictionary of canvases and CanvasTextures (meshes).
            //
            // Duplicated canvas names are handled by appending underscores to the name... for now.
            let canvasMaterialNames : {[canvasName: string]: string[]} =
                (<CanvasTileList[]>this.incomplete.json.canvasTiles)
                .reduce((map:{[canvasName:string]:string[]}, obj:CanvasTileList) => {
                    let canvasName = obj.canvas;
                    // Keep adding _ until name is unique.
                    while (map.hasOwnProperty(canvasName))
                        canvasName += "_";
                    map[canvasName] = obj.tiles;
                    return map;
                }, {});
            for (let canvasName in canvasMaterialNames)
                for (let materialName of canvasMaterialNames[canvasName])
                    // Test is *only* required for manually hacked-up scenes like RWOcartoonGallery.
                    // Normal exports should be fine.
                    // RWOcartoonGallery: multiple tiles of one canvas all share the same material.
                    // No tiles are shared between canvases though, so everything else should just work.
                    if (this.mtlLoader.materialCreator.materials.hasOwnProperty(materialName))
                        canvasNameByMaterialUUID[this.mtlLoader.materialCreator.materials[materialName].uuid] = canvasName;

            for (let object of this.incomplete.arrayofobjects) {
                let canvasName = canvasNameByMaterialUUID[(<Material>object.material).uuid];
                let ct : CanvasTexture = CanvasTexture.FromMesh(<Mesh>object);
                Object.assign(ct, CanvasTexture.prototype);
                ct.m_canvasName = canvasName; 
                if (!this.m_canvases.hasOwnProperty(canvasName))
                    this.m_canvases[canvasName] = new Canvas;
                this.m_canvases[canvasName].m_cts.push(ct);
            }
        }
        else {
            // Absent real information about canvases, make one per CanvasTexture. This means
            // that tiled canvases will look like a set of independent canvases.
            for (let ctIndex : number = 0; ctIndex < this.incomplete.arrayofobjects.length; ++ctIndex) {
                let canvasName = "canvas" + ctIndex;
                let mesh : Mesh = this.incomplete.arrayofobjects[ctIndex];
                if(mesh != null) {
                    // Downcast... or really, mutate object's type to subclass type.
                    let ct : CanvasTexture = CanvasTexture.FromMesh(mesh);
                    this.m_canvases[canvasName] = new Canvas;
                    this.m_canvases[canvasName].m_cts.push(ct);
                    canvasNameByMaterialUUID[(<Material>ct.material).uuid] = canvasName;
                }
            }
        }
        // Now that we have a canvas<->material association defined, fill in:
        // 1) the canvasID uniforms for picking
        // 2) pulse boolean
        for ( let canvasIndex = 0; canvasIndex < Object.keys(this.m_canvases).length; ++canvasIndex ) {
            const canvasName : string = Object.keys(this.m_canvases)[canvasIndex];
            const canvas : Canvas = this.m_canvases[canvasName];
            const pickColor = new Color(canvasIndex + 1);
            for (const ct of canvas.m_cts) {
                let mat = <ShaderMaterial>ct.material;
                mat.uniforms.pickID_r.value = pickColor.r;
                mat.uniforms.pickID_g.value = pickColor.g;
                mat.uniforms.pickID_b.value = pickColor.b;
            }

            // If the canvas has 'pulse' in the name, add it to the pulse materials list.
            canvas.m_bPulse = canvasName.includes("pulse");
            this.bHasPulseCanvases = this.bHasPulseCanvases || canvas.m_bPulse;
        }


        // Now that we have an organized list of canvases + CTs:
        // 1) Compute canvas plane
        // 2) Add geometry to scene
        // 3) Build up a scene bounding sphere
        let sphere : Sphere = null;
        for (let canvasName of Object.keys(this.m_canvases)) {
            let canvas : Canvas = this.m_canvases[canvasName];
            let ct : CanvasTexture = canvas.m_cts[0];

            // Very old scenes may not have normals...
            if (bsp.bHasNormals) {
                const normalAttrib = (<BufferGeometry>ct.geometry).getAttribute('normal').array;
                const positionAttrib : number[] = <number[]>(<BufferGeometry>ct.geometry).getAttribute('position').array;
                canvas.m_plane = new Plane;
                canvas.m_plane.setFromNormalAndCoplanarPoint(
                    new Vector3(normalAttrib[0], normalAttrib[1], normalAttrib[2]),
                    new Vector3(positionAttrib[0], positionAttrib[1], positionAttrib[2]));
            }

            for (let ct of this.m_canvases[canvasName].m_cts) {
                ct.geometry.computeBoundingSphere();
                if (sphere == null)
                    sphere = ct.geometry.boundingSphere.clone();
                else
                    MergeSpheres(sphere, ct.geometry.boundingSphere);
                this.scene.add(ct);
            }
        }

        // Parse list of canvas links. Unfortunately specified by material name.
        if ( this.incomplete.json.links != null )
        {
            for (let cl of this.incomplete.json.links) {
                if (this.mtlLoader.materialCreator.materials.hasOwnProperty(cl.material)) {
                    let canvasName : string = canvasNameByMaterialUUID[this.mtlLoader.materialCreator.materials[cl.material].uuid];
                    this.m_canvases[canvasName].m_linkUrl = cl.link;
                }
            }
        }

        // Parse list of canvas visToggles.
        if ( this.incomplete.json.visToggles != null )
        {
            for (let cl of this.incomplete.json.links) {
                if (this.mtlLoader.materialCreator.materials.hasOwnProperty(cl.material)) {
                    let canvasName : string = canvasNameByMaterialUUID[this.mtlLoader.materialCreator.materials[cl.material].uuid];
                    this.m_canvases[canvasName].m_visToggleCanvasName = cl.vis;
                }
            }
        }

        this.setSceneInfoAndBookmarks(this.incomplete.json);
        this.arrayofobjects = this.incomplete.arrayofobjects;
        this.bsp = bsp;

        let parsePlane = function(json: any, canvases: {[name: string]: Canvas}): Plane
        {
            let plane : Plane = null;
            if (canvases.hasOwnProperty(json.canvasName)) {
                plane = canvases[json.canvasName].m_plane.clone();
                let offsetSign : number = 1;
                if (json.hasOwnProperty('flip') && json.flip != 'false') {
                    plane.negate();
                    offsetSign = -1;
                }
                plane.constant -= json.hasOwnProperty('offset') ? +json.offset * offsetSign : 0.2;
            }
            return plane;
        }
        // Only works on scenes with a "real" m_canvases export.
        if (this.incomplete.json.planeConstraints) {
            let constraint : any;
            for (constraint of this.incomplete.json.planeConstraints) {
                let plane = parsePlane(constraint, this.m_canvases);
                if (plane) {
                    let finiteEdges : Plane[] = [];
                    if (constraint.hasOwnProperty('finiteEdges')) {
                        for (let edge of constraint.finiteEdges) {
                            let edgePlane = parsePlane(edge, this.m_canvases);
                            if (edgePlane)
                                finiteEdges.push(edgePlane);
                            else
                                console.log('Edge canvas not found: ' + edge.canvasName);
                        }
                    }
                    this.controls.m_cameraConstraints.m_constraints.push(
                        new PlaneConstraint(constraint.name || constraint.canvasName,
                            plane, finiteEdges,
                            constraint.spring || -1,
                            constraint.disableDepth || -1)
                    )
                } else
                    console.log('Constraint canvas not found: ' + constraint.canvasName);
            }            
        }

        this.incomplete = null;

        this.controls.boundingSphere = sphere;
        this.controls.hand.m_BoundingSphere = sphere;
        this.controls.hand.m_bBoundingSphereSet = true;

        // TODO: just reuse planeConstraint stuff
        if (this.options.groundPlane != null && this.options.groundPlane.length > 0)
        {
            if (this.m_canvases.hasOwnProperty(this.options.groundPlane)) {
                // TODO: expose 0.2 as an option
                const rawPlane = this.m_canvases[this.options.groundPlane].m_plane;
                let plane = rawPlane.clone();
                plane.constant -= 0.2;
                this.controls.m_cameraConstraints.m_constraints.push(
                    // hard floor constraint
                    new PlaneConstraint(this.options.groundPlane, plane, [], -1)
                )
                if (this.options.ceilingHeight > 0) {
                    plane = rawPlane.clone().negate();
                    plane.constant += 0.2 - this.options.ceilingHeight;
                    this.controls.m_cameraConstraints.m_constraints.push(
                        // soft ceiling constraint
                        new PlaneConstraint('[ceiling]', plane, [], 100)
                    )
                }
            } else
                console.log('Ground plane canvas not found: ' + this.options.groundPlane);
        }
        // If no stage definitions present, use auto-calculated center
        for (let stage of this.stages)
            if (stage.center == null)
                stage.center = this.interimCenter.clone();
        this.controls.arcball.SetCenter(this.stages[0].center);
        //console.timeEnd( 'activatePendingScene' );
    }

    // Only in effect when fullscreen is enabled
    onWindowResize = () => {
        if (navigator.userAgent.match(/iPhone|iPod/i)) {
            // Several problems with iPhone/iPod devices: the "height: 100%" container elements don't
            // automatically update their height in a few situations
            // 1) When flipping from portrait to landscape (typically with no top/bottom button bar)
            // 2) In landscape, when the button bars appear
            //
            // See here for some discussion of the issues.
            // https://medium.com/@susiekim9/how-to-compensate-for-the-ios-viewport-unit-bug-46e78d54af0d
            // https://bugs.webkit.org/show_bug.cgi?id=141832
            // https://benfrain.com/the-ios-safari-menu-bar-is-hostile-to-web-apps-discuss/
            document.body.style.height = window.innerHeight + 'px';
            this.onResize(true);
            // I really don't understand why this is necessary, but it appears to be for
            // portrait->landscape->portrait sequence of device orientations.
            window.setTimeout(() => { this.onResize(true); }, 500);
        }
        else
            this.onResize(true);
    }
    onResize(bForce: boolean = false)
    {
        if (this.renderer == null || this.renderer.domElement == null || this.renderer.domElement.clientHeight <= 0)
            return;
        let lastSize = new Vector2;
        this.renderer.getDrawingBufferSize(lastSize);
        // Parent container is used by HTML to position/size the canvas. Canvas' size will be synced by this
        // routine.
        const clientWidth:number = this.options.fullscreen ? window.innerWidth : this.renderer.domElement.parentElement.clientWidth;
        const clientHeight:number = this.options.fullscreen ? window.innerHeight : this.renderer.domElement.parentElement.clientHeight;
        if (!bForce &&
            clientWidth * this.renderer.getPixelRatio() == lastSize.width &&
            clientHeight * this.renderer.getPixelRatio() == lastSize.height)
            return;

        //console.log("OnResize " + clientWidth + "x" + clientHeight);
        this.camera.aspect = clientWidth / clientHeight;
        var flipFov = function (a: number, fov: number): number {
            return 2 * Math.atan(Math.tan(fov * _Math.DEG2RAD / 2) * a) * _Math.RAD2DEG;
        }
        const bViewportLandscape: boolean = clientWidth >= clientHeight;
        if (this.mcxVersion >= 2) {
            // Modern behavior: author has told us what the intended aspect ratio is.
            // - When window orientation matches scene orientation, we will get a "Hor+" type of cropping
            //   full match of vertical field-of-view, more/less horizontal cropping depending on difference
            //   of viewport.
            // - When window orientation is opposite, we get:
            //   scene: landscape, window: portrait - camera pulls back to VFOV=80, should look okay.
            //   scene: portrait, window: landscape - camera zooms in to VFOV=60, should look okay.
            this.camera.fov = bViewportLandscape ? 60 : this.fovvPortrait;
        } else {
            // Legacy file behaviour... at this stage, we did not really have much support for portrait
            // authoring. No way to choose "intended" scene orientation, aspect ratio or field-of-view.
            // (Aspect/orientation were guessed by the device orientation when bookmarks were snapped.)
            const desiredFov_v = 60;
            const desiredFov_h = flipFov(this.sceneAspectRatio, desiredFov_v);
            // Window orientation matches scene orientation: almost gives back the same result,
            //    as this.camera.aspect is close to this.sceneAspectRatio
            // Window orientation does not match scene orientation: gives a very different result,
            //    particularly pulling back for scene=landscape, window=portrait.

            //const impliedFov_h = flipFov(this.camera.aspect, desiredFov_v);
            const impliedFov_v = flipFov(1 / this.camera.aspect, desiredFov_h);
            this.camera.fov = .5 * (desiredFov_v + impliedFov_v);
        }

        this.camera.updateProjectionMatrix();
        // FYI, with other params this would also update the DOM element's size.
        this.renderer.setSize(clientWidth, clientHeight);
        this.controls.OnResize();
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.RedrawRequested = true;
    }

    setTileOpacity(key: string, opacity: number, duration: number = 0) {
        var tiles = this.mtlLoader.materialCreator.materials;
        if (tiles.hasOwnProperty(key)) {

            var animationIsActive = this.opacityAnimations.hasOwnProperty(key);
            if (duration > 0 && opacity != tiles[key].opacity) {
                // animate towards new opacity if we aren't already heading there
                if (!animationIsActive || opacity != this.opacityAnimations[key].endOpacity) {
                    var currentTime = this.clock.getElapsedTime();
                    var currentOpacity = tiles[key].opacity;
                    this.opacityAnimations[key] = new OpacityAnimation(currentTime, currentTime+duration, currentOpacity, opacity);
                }                     
            } else {
                // just set it now and remove the active animation if necessary
                if (animationIsActive)
                    delete this.opacityAnimations[key];
                tiles[key].opacity = opacity;
            }
        }        
    }

    setAnimationScrubT(t: number, isUserScrub: boolean = true) {
        // if isUserScrub is false, then we are just setting up elapsed time, camera and opacities for the first time
        var tileOpacities = {};
        this.cameraAnimator.setAnimationScrubT(this.camera, tileOpacities, t);
        if (this.linkVizMode) {
            var tiles = this.mtlLoader.materialCreator.materials;
            for (var key in tiles) {
                this.setTileOpacity(key, tileOpacities[key]);
            }
        }
        if (isUserScrub) {
            this.RedrawRequested = true;
            this.scrubbing = true;
            this.animate();
        } else {
            this.syncControlsWithBookmark();
        }
    }
    performPick(): boolean {
        var bResult : boolean = false;
        if ( this.bIsPickEnqueued )
        {
            console.time( 'performPick' );

            const pixelRatio : number = this.renderer.getPixelRatio();
            const debug : boolean = false;
            if (!debug)
                this.camera.setViewOffset(
                    this.renderer.getContext().drawingBufferWidth,
                    this.renderer.getContext().drawingBufferHeight,
                    this.pickClientX * pixelRatio | 0,
                    this.pickClientY * pixelRatio | 0,
                    1, 1
                );

            {
                this.bsp.SortGeometries(this.arrayofobjects, this.camera);
                
                // Indicate we're about to draw for picking.
                for (let canvasName of Object.keys(this.m_canvases))
                    for (let ct of this.m_canvases[canvasName].m_cts)
                        (<MCShaderMaterial>ct.material).uniforms.usePickID.value = 1;

                // Render to the pick target.
                var pickTarget = this.pickTarget;
                var pickBuffer = this.pickBuffer;
                if (debug) {
                    pickTarget = new WebGLRenderTarget( this.renderer.getContext().drawingBufferWidth, this.renderer.getContext().drawingBufferHeight, {
                        depthBuffer: false,
                        stencilBuffer: false,
                        generateMipmaps: false
                    } );
                    pickBuffer = new Uint8Array(4*pickTarget.width*pickTarget.height);
                }
                this.renderer.setRenderTarget( pickTarget );
        
                this.renderer.render( this.scene, this.camera );
                this.renderer.setRenderTarget( null );

                // Readback.
                this.renderer.readRenderTargetPixels( pickTarget, 0, 0, pickTarget.width, pickTarget.height, pickBuffer );
                if (debug) {
                    // For debugging: click on debugimage in elements, open in new tab
                    var uac = new Uint8ClampedArray(pickBuffer, pickTarget.width, pickTarget.height);
                    var imageData = new ImageData(uac, pickTarget.width, pickTarget.height);
                    var debugcanvas : HTMLCanvasElement = <HTMLCanvasElement>document.getElementById('debugcanvas');
                    if (debugcanvas == null) {
                        debugcanvas = <HTMLCanvasElement>document.createElement('canvas');
                        debugcanvas.setAttribute('id', 'debugcanvas');
                        debugimage = <HTMLImageElement>document.createElement('image');
                        debugimage.setAttribute('id', 'debugimage');
                    }
                    debugcanvas.width = pickTarget.width;
                    debugcanvas.height = pickTarget.height;
                    debugcanvas.getContext('2d').putImageData(imageData, 0, 0);
                    var debugimage = <HTMLImageElement>document.getElementById('debugimage');
                    debugimage.src = debugcanvas.toDataURL();
                    // TODO: also pull appropriate pixel out of the buffer
                }
                const pickID =
                    (this.pickBuffer[0] << 16) |
                    (this.pickBuffer[1] <<  8) |
                    (this.pickBuffer[2]      );
                if ( pickID == 0x00ffffff || pickID == 0x00000000 )
                    console.log( 'picked background ' +  this.pickBuffer );
                else if ( pickID > 0 && pickID <= Object.keys(this.m_canvases).length )
                {
                    // TODO: trigger the action associated with the canvas pick here.
                    console.log( 'picked ID ' + pickID + ' ' + this.pickBuffer );
                        const canvasName : string = Object.keys(this.m_canvases)[pickID-1];
                    const canvas : Canvas = this.m_canvases[Object.keys(this.m_canvases)[pickID-1]];
                    const link : string = canvas.m_linkUrl;
                    const canvasToVisToggle : string = canvas.m_visToggleCanvasName;
                    if ( link != null )
                    {
                        console.log( 'picked link canvas ' + canvasName + ' ' + link );
                        open( link, '_blank' );
                        bResult = true;
                    }
                    if ( canvasToVisToggle != null )
                    {
                        console.log( 'picked visToggle canvas ' + canvasName + ' ' + canvasToVisToggle );
                        //console.log( 'checking against ' + this.canvasTiles.length + ' entries...' );
                        if (this.m_canvases.hasOwnProperty(canvasToVisToggle)) {
                            const canvas = this.m_canvases[canvasToVisToggle];
                            for (let ct of canvas.m_cts) {
                                let material = <Material>ct.material;
                                material.visible = !material.visible;
                            }
                        }
                        bResult = true;
                    }
                    if (!bResult)
                        console.log( 'picked inactive canvas ' + canvasName);
                }
                else
                    console.log( 'picked invalid ID ' + pickID);

                // Revert picking uniform now that we're finished with the pick draw.
                for (let canvasName of Object.keys(this.m_canvases))
                    for (let ct of this.m_canvases[canvasName].m_cts)
                        (<MCShaderMaterial>ct.material).uniforms.usePickID.value = 0;
            }

            this.camera.clearViewOffset();
            this.bIsPickEnqueued = false;

            console.timeEnd( 'performPick' );
        }
        return bResult;
    }

    hasPickableCanvases(): boolean {
        for (var canvasName of Object.keys(this.m_canvases)) {
            var c = this.m_canvases[canvasName];
            if (c.m_linkUrl != null || c.m_visToggleCanvasName != null)
                return true;
        }
        return false;
    }

    private setTileOpacitiesBasedOnCameraPosition() {

        // set canvas opacities based on where the camera is
        // assumes linkVizMode is true
        var tileOpacities = {};

        this.cameraAnimator.GetCanvasOpacitiesFromCameraPosition(this.camera, tileOpacities);

        var tiles = this.mtlLoader.materialCreator.materials;
        for (var key in tiles) {
            this.setTileOpacity(key, tileOpacities[key], 1);
        }
    }

    private setTileOpacitiesBasedOnBookmark(index: number) {

        // assumes linkVizMode is true
        var tileOpacities = this.cameraAnimator.m_animationBookmarks.GetFrame(index).GetTileOpacities();
        var tiles = this.mtlLoader.materialCreator.materials;
        for (var key in tiles) {
            this.setTileOpacity(key, tileOpacities[key]);
        }
    }

    protected collabAnimate(deltatime: number) {
    }
    suppressRender() {
        this.m_bRenderSuppressed = true;
    }
    resumeRender() {
        this.m_bRenderSuppressed = false;
        this.RedrawRequested = true;
    }
    private m_animationFrameHandle : number = undefined;
    private animate = () => {
        //console.time('animate');
        this.m_animationFrameHandle = requestAnimationFrame(this.animate);
        var deltatime = this.clock.getDelta();
        var bRedraw = this.RedrawRequested;

        var beginTime : number = (performance || Date).now();

        //if (this.reloadFlag)
        //    console.log('animate()');
        this.RedrawRequested = false;

        if (this.m_bRenderSuppressed)
            return;

        // If fresh controls input just came in, halt any existing playback.
        if (this.controls.m_bShouldHaltPlayback) {
            this.playPause('paused');
            this.controls.m_bShouldHaltPlayback = false;
        }
        else if (this.controls.m_bShouldTogglePlayback) {
            this.playPause(toggleMode(this.mode));
            this.controls.m_bShouldTogglePlayback = false;
        }

        if (this.cameraAnimator.IsAnimating()) {
            var tileOpacities = {};
            this.cameraAnimator.AnimateStep(this.camera, tileOpacities, deltatime);
            if (this.linkVizMode || !this.cameraAnimator.IsAnimatingBookmarks()) {
                var tiles = this.mtlLoader.materialCreator.materials;
                for (var key in tiles) {
                    this.setTileOpacity(key, tileOpacities[key]);
                }
            }
            this.syncControlsWithBookmark();
            if (this.cameraAnimator.IsAnimationCompleted()) 
                // At the time this is called, stage and controls are already set to the final bookmark's stage. Leave it.
                this.playPause('paused');
                
            bRedraw = true;
        }

        if (this.options.vizMode == 'positionBased' && !this.cameraAnimator.IsAnimating() && 
            !this.cameraAnimator.IsOnBookmarkPath() && this.linkVizMode) {
            this.setTileOpacitiesBasedOnCameraPosition();
		}
    
        // go through and update the tile opacity animations
        var elapsedTime = this.clock.getElapsedTime();
        var tiles = this.mtlLoader.materialCreator.materials;
        for (var key in this.opacityAnimations) {
            var animation = this.opacityAnimations[key];
            if (elapsedTime >= animation.endTime) {
                // time to delete the animation
                tiles[key].opacity = animation.endOpacity;
                delete this.opacityAnimations[key];
                bRedraw = true;
            } else if (elapsedTime >= animation.startTime) {
                // calculate animation opacity at this elapsed time
                var deltaT = elapsedTime-animation.startTime;
                var duration = animation.endTime - animation.startTime;
                var alpha = deltaT / duration;
                var newOpacity = animation.startOpacity + alpha * (animation.endOpacity - animation.startOpacity);
                tiles[key].opacity = newOpacity;
                bRedraw = true;
            }
        }

        ///////////////////////////////////////////////////////////////////////////////////////////////////
        // Advance the pulse animation.
        if ( this.bHasPulseCanvases ) {
            this.pulseTime += deltatime;
            // If it's been 50ms since the last pulse redraw, redraw now.
            if ( this.pulseTime > this.lastPulseRedrawTime + .05 ) {
                this.lastPulseRedrawTime = this.pulseTime;
                bRedraw = true;
            }
        }

        ///////////////////////////////////////////////////////////////////////////////////////////////////
        // Advance load opacities
        for (let canvasName in this.m_canvases)
            for (let ct of this.m_canvases[canvasName].m_cts) {
                let mat = <MCShaderMaterial>ct.material;
                if( mat.m_loadOpacity < 1.0 && mat.m_bLoaded ) {
                    mat.m_loadOpacity += 2.0*deltatime; // Fade-in over a half second.
                    if ( mat.m_loadOpacity > 1.0 )
                        mat.m_loadOpacity = 1.0;
                    bRedraw = true; // A material's load opacity was animated this frame, so redraw.
                }
            }

        ///////////////////////////////////////////////////////////////////////////////////////////////////
        // Update controls.
        if (!this.cameraAnimator.IsAnimating() && !this.scrubbing) {
            if (this.controls.animateRubberBand(deltatime))
                bRedraw = true;
        }
        else
            // Disable auto-evaluation until user interacts with the scene.
            this.controls.m_bDisablePassiveConstraintEvaluation = true;

        ///////////////////////////////////////////////////////////////////////////////////////////////////
        // Redraw.
        if (bRedraw) {
            // Non-fullscreen: we just passively keep the rendering in sync with the HTML canvas size.
            if (!this.options.fullscreen)
                this.onResize();
            this.bsp.SortGeometries(this.arrayofobjects, this.camera);

            const pulsesPerSec = 0.666;
            const pulseAlpha = Math.pow( 0.5 * ( Math.cos( this.pulseTime * 6.2832 * pulsesPerSec ) + 1.0 ), 0.2 );

            // Set our uniforms.
            for (let canvasName of Object.keys(this.m_canvases)) {
                var canvas = this.m_canvases[canvasName];
                    for (let ct of canvas.m_cts) {
                        var material = <MCShaderMaterial>ct.material;
                        var opacity : number = material.opacity * material.m_loadOpacity;
                        // If the material is visible, modulate by the link pulse if necessary.
                        if ( canvas.m_bPulse && opacity > 0.05 )
                            opacity *= pulseAlpha;
                    material.uniforms.opacity.value = opacity;
                    material.uniforms.zNear.value = this.camera.near;
                }
            }

            if (this.bIsPickEnqueued)
                if (!this.performPick())
                    this.playPause(toggleMode(this.mode));

            this.renderer.render(this.scene, this.camera);

            // If the user is interacting, sacrifice framerate to get lower latency. Similar to behavior in
            // SimpleScene's getFinishAfterSwapBuffers.
            if (this.controls.interactionInProgress())
                this.renderer.getContext().finish();

            //console.log('Memory usage: # geo = ' + this.renderer.info.memory.geometries + ', # tex = ' + this.renderer.info.memory.textures + '\n');

            // If we're redrawing with no UI interaction, and no camera interaction,
            // let host know that the camera moved.
            // Typically due to rubber-band motion etc.
            // (End of UI interaction and end of playback will both trigger completions of their own)

            if (!this.controls.interactionInProgress() && !this.cameraAnimator.IsAnimating()) {
                // TODO: need a way to signal to client that camera moved, but that it was *not* an interaction.
                // And also exclude pulses, load opacities, etc.
                //this.dispatchEvent({type:'interactioncomplete', attachment:new InteractionData(this)});
            }
        }

        if (this.bIsCollabSession)
            this.collabAnimate(deltatime);

        //console.timeEnd('animate');
    }

    startTransitionToPreviousBookmark()
    {
        this.startTransitionToBookmark(this.cameraAnimator.GetPrevBookmarkIndex());
    }
    startTransitionToNextBookmark()
    {
        this.startTransitionToBookmark(this.cameraAnimator.GetNextBookmarkIndex());
    }
    //
    // Set camera position/orientation to bookmark
    // Also set active stage ID and the associated camera limits.
    // 
    private setCameraToBookmark(bookmarkIndex: number) {
        var b = this.bookmarks[bookmarkIndex];
        this.camera.quaternion.copy(b.quat);
        this.camera.position.copy(b.vec);

        // Initialise the opacity to that of the first bookmark.
        if (this.linkVizMode) {
            console.log('Setting link visibility...\n');
            this.setTileOpacitiesBasedOnBookmark(0);
        }
        this.syncControlsWithBookmark();
    }
    startTransitionToBookmark(bookmarkIndex: number, playBookmarkAnimationOnComplete: boolean = false) {
        if (this.bookmarks.length == 0) return;
        
        this.scrubbing = false;
        this.cameraAnimator.SetupAnimation_UIFromCameraToBookmark(this.mtlLoader.materialCreator.materials, this.linkVizMode, this.camera, bookmarkIndex, playBookmarkAnimationOnComplete);
        // tell controls to use a suitable lookat target
        this.syncControlsWithBookmark();
        if (!playBookmarkAnimationOnComplete) {
            this.mode = 'paused';
            this.dispatchEvent({ type: 'playPause', attachment:this.mode });
        }
    }
    playPause(newMode: MCMode) {
        this.scrubbing = false;
        var oldMode: MCMode = this.mode;
        this.mode = newMode;
        this.dispatchEvent({ type: 'playPause', attachment:this.mode })
        if (this.mode != oldMode) {
            if (this.mode == 'playing')
                this.PlayBookmark();
            else {
                this.PauseBookmark();
                this.dispatchEvent({ type: 'interactioncomplete', attachment:new InteractionData(this)});
            }
        }
        // TODO: prevent idle timer events during playback?
    }
    // these are from MCSimpleScene.cpp
    private PlayBookmark() {
        this.cameraAnimator.CheckSetAnimationCompleted();

        var tiles = this.mtlLoader.materialCreator.materials;

        if (this.cameraAnimator.IsAnimationResumable()) {
            // we are paused in a bookmark animation. resume animation
            this.cameraAnimator.ResumeAnimation(tiles, this.linkVizMode, this.camera);
        } else if (this.cameraAnimator.IsAnimating() && !this.cameraAnimator.IsAnimatingBookmarks()) {
            // we are in the middle of a ui animation
            if (this.cameraAnimator.GetBookmarkSplinePosAfterAnimation() >= 0)
                // already heading towards a bookmark. just set the play after animation flag
                this.cameraAnimator.SetBookmarkPlayAfterAnimation(true);
            else if (this.bookmarks.length > 0)
                // not going towards a bookmark, we want to head to bookmark 0 if it exists
                this.cameraAnimator.SetupAnimation_UIFromCameraToBookmark(tiles, this.linkVizMode, this.camera, 0, true);
        } else {
            // not paused and not in the middle of a ui animation
            if (this.bookmarks.length > 0) {
                var animationCompleted = this.cameraAnimator.IsAnimationCompleted();
                // no need to reload bookmarks
                this.cameraAnimator.SetupAnimation_Bookmarks(tiles, null, true);
                if (!this.cameraAnimator.IsOnBookmarkPath() || animationCompleted) {
                    this.cameraAnimator.SetupAnimation_UIFromCameraToBookmark(tiles, this.linkVizMode, this.camera, 0, true);
                }
            }
        }
    }
    private PauseBookmark() {
        this.cameraAnimator.PauseAnimation();
    }
    private syncControlsWithBookmark() {
        // Authoring Nav Mode (and egocentric): Hand manages the center, and there's a fixed
        // constraint frame for the whole animation.
        if (this.controls.enableAuthoringNavMode) {
            this.stageId = 1;
            this.controls.limits.copy(this.stages[this.stageId]);
            this.controls.constraintFrame.rotation.copy(this.bookmarks[1].quat);
            this.controls.constraintFrame.translation.copy(this.bookmarks[1].vec);
            this.controls.hand.m_PanMaxPercent = this.stages[this.stageId].PanMax;
            return;
        }
        // var closestBookmarkIndex = this.cameraAnimator.getBookmarkClosestToCameraPoint(this.camera);
        this.stageId = this.bookmarks[this.cameraAnimator.GetStageIdBookmarkIndex()].stageId;
        // this.stageId = this.bookmarks[closestBookmarkIndex].stageId;

        // Update controls parameters based on where we are along the path
        // Angle limits depend on the stage
        this.controls.limits.copy(this.stages[this.stageId]);
        // console.log(this.controls.limits);
        // Unusual: both the constraint frame and the center are based on the *mid-bookmark* position/orientation, not the
        // actual bookmark position/orientation.
        this.controls.constraintFrame.rotation.copy(this.camera.quaternion);
        this.controls.constraintFrame.translation.copy(this.camera.position);
        //console.log('setCameraToCurrentBookmark :', this.controls.limits.Azimuth, ':', this.controls.limits.AzimuthRange);
        var center: Vector3 = this.calcControlCenterFromStage(this.camera.position, this.camera.quaternion);
        this.controls.arcball.SetCenter(center);
        this.controls.m_arcballOrigCenter.copy(center);
        this.controls.hand.m_PanMaxPercent = this.stages[this.stageId].PanMax;
        this.controls.hand.SetOrigin(center);
    }
    // Given the position and orientation, find the appropriate rotation center
    private calcControlCenterFromStage(position: Vector3, rotation: Quaternion): Vector3 {
        var dir : Vector3 = new Vector3(0, 0, -1);
        dir.applyQuaternion(rotation);
        // Set lookat target to be on the bookmark view direction vector. Distance depends on two cases below.
        var delta : Vector3 = this.stages[this.stageId].center.clone().sub(position);
        if (dir.dot(delta) >= 0.1) {
            // Case 1: "inward" looking, some distance from the scene centroid. Project the centroid onto the viewpoint
            // vector and use that as the lookat target / rotation centre
            dir.multiplyScalar(dir.dot(delta));
        } else {
            // Case 2: "outward" looking, where the scene centroid is behind the camera. Set the target to be just slightly
            // in front of the camera for both lookat target and rotation centre.
            dir.multiplyScalar(this.bIsCollabSession ? 0.5 : 0.1);
        }
        this.controls.depthBookmark = dir.length();
        return position.clone().add(dir);
    }

    // From C++ SimpleScene
    SetNavModeCenter()
    {
        const bCenterManuallySet : boolean = false;
        if ( !bCenterManuallySet ) {
            const result = this.NavmodeComputedCenter();
            this.SetNavmodeCenterFromPoint( result.center, result.projectCenterOntoViewVector);
        }
    
    }
    // From C++ SimpleScene
    private SetNavmodeCenterFromPoint(InPoint: Vector3, projectCenterOntoViewVector: boolean)
    {
        var center : Vector3 = InPoint.clone();

        // DavidP WebViewer testing via algoVariantTest:
        // 0: mimics C++ Player, with projection onto the constraint frame's Z vector. Rotation center
        //    is often far offscreen.
        // 1: no projection - just uses estimated centroid as the center. (Centroids are pretty crappy
        //    right now though, due to no stroke data.)
        // 2: projection onto the view vector center. This sometimes feels weird, if the view vector is pointing
        //    out into space, nowhere near the scene.
        //
        // Best path forward is probably to get better centroid data out of authoring, then use #1.
        const algoVariantTest : number = 2;

        var bPlayer : boolean = !this.controls.enableAuthoringNavMode;
        if (this.controls.enableAuthoringNavMode && algoVariantTest == 1)
            bPlayer = false;
        // for authoring tool, we only want to do this if m_bProjectCenterOntoViewVector is true, so we can see how player will behave (without limits though)
        // projectCenterOntoViewVector will be set to true when:
        // 1. auto-set case: when SetNavmodeCenter() is called and the camera is on the bookmark path and at least one of the two neighboring bookmarks has a center set
        // 2. manual set case: when SetNavModeCenterFromScreenPoint() is called and we are on a bookmark
        if ( bPlayer || projectCenterOntoViewVector )
        {
            // Player: all rotation, panning and zooming is based on the constraint (bookmark) frame. The
            // requested center may not actually correspond to the constraint frame's view vector - so
            // project onto that vector. This ensures we are consistently pivoting around the screen center.

            var r : Ray = new Ray(this.controls.constraintFrame.translation, this.controls.constraintFrame.getZAxis3().negate());
            if (this.controls.enableAuthoringNavMode && algoVariantTest == 2)
                r.origin.copy(this.camera.position);
            const rayT : number = r.ComputeTFromPos(InPoint);
            if (rayT > 0) {
                // Requested center is in front of the bookmark/constraint camera.
                center = r.GetPos(rayT);
                //DPrintf(TEXT("Shifted center onto bkmk vector at rayT=%.2f, dist=%.2f\n"), rayT, m_Center.Dist(realCenter));
            }
            else {
                // Requested center is behind the bookmark/constraint camera. This will give weird behavior -
                // so don't do it. Instead, just pivot about a point 1.0 units ahead of the bookmark.
                center = r.GetPos(1);
                //console.log('Ignoring requested center, rayT=' + rayT.toFixed(2));
            }
        }
        this.controls.arcball.SetCenter(center);
        this.controls.hand.SetOrigin(center);
    }

    // From C++ SimpleScene
    NavmodeComputedCenter(): { center: Vector3, projectCenterOntoViewVector: boolean }
    {
        const bookmarkPathSplinePos : number = this.cameraAnimator.GetBookmarkSplinePosAfterAnimation();
        var result : { center: Vector3, projectCenterOntoViewVector : boolean } =
            { center: null, projectCenterOntoViewVector: false};
        
        if (   bookmarkPathSplinePos >= 0
            && bookmarkPathSplinePos < this.bookmarks.length
            && this.cameraAnimator.IsOnBookmarkPath() )
        {
            // we are on the bookmark path
            // note: could have 1 bookmark, and centers may not be set
    
            // interpolate centers based on where we are in the bookmark path
    
            // get interpolation indices. i should equal j for the case of 1 bookmark
            const i : number = Math.floor(bookmarkPathSplinePos);
            const j : number = (i+1)%this.bookmarks.length;
    
            const iBmark : Bookmark = this.bookmarks[i];
            const jBmark : Bookmark = this.bookmarks[j];
            if ( iBmark.IsCenterOfRotationSet() || jBmark.IsCenterOfRotationSet() )
            {
                // at least one center is set, interpolate. 
                // if the other center is not set, then we will calculate it based on the bookmark view RBT
                result.projectCenterOntoViewVector = true;
                const t : number = bookmarkPathSplinePos-i;
                const iBmarkCenterOfRotation : Vector3 = ( iBmark.IsCenterOfRotationSet() ? iBmark.GetCenterOfRotation() : this.GetViewCentroid( iBmark.GetRBT() ) );
                const jBmarkCenterOfRotation : Vector3 = ( jBmark.IsCenterOfRotationSet() ? jBmark.GetCenterOfRotation() : this.GetViewCentroid( jBmark.GetRBT() ) );
                result.center = iBmarkCenterOfRotation.lerpVectors( iBmarkCenterOfRotation, jBmarkCenterOfRotation, t );
            } else
                // if both centers are not set, use the view centroid
                result.center = this.GetViewCentroid(CameraUtils.GetRBT(this.camera));
    
        }
        else
        {
            // just calculate center the usual way
            result.center = this.GetViewCentroid( CameraUtils.GetRBT(this.camera) );
        }
        return result;
    }

    IsLooping(): boolean
    {
        return this.cameraAnimator.IsLooping();
    }
    SetLooping(bLoop: boolean)
    {
        return this.cameraAnimator.SetBookmarkLooping(bLoop);
    }

    // Ported from C++ SimpleScene
    private GetViewCentroid(rbt: RBT): Vector3
    {
        var cam = this.camera.clone();
        
        cam.position.copy(rbt.translation);
        cam.quaternion.copy(rbt.rotation);
    
        var NumVisibleCTs : number = 0;
        var cumulative : Vector3 = new Vector3( 0, 0, 0 );
    
//        if ( GCore.IsPlayer() )
        {
            // Player doesn't have strokes. Need a different way to establish a centroid.
            // The idea is: test the bounds of each canvas/tile to see which canvas/tiles
            // are visible in frustum. (Actually, take ~100 samples on each canvas/tile).
            // This approximates what happens in authoring, where all stroke centroids are tested on
            // each canvas.
            //
            // A faster approach might be to render to a depth map, average all non-background
            // pixel positions and depths. This wouldn't account for occluded content though -
            // you might need to calc both nearest/farthest point per pixel.
            //
            // TODO: backport this to C++ (Windows Player).
            var key : string;
            var frustum : Frustum = new Frustum;
            // matrixWorldInverse is the view matrix
            frustum.setFromMatrix((new Matrix4).multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse));
            for ( let canvasName of Object.keys(this.m_canvases)) {
                let canvas = this.m_canvases[canvasName];
                for ( let ct of canvas.m_cts ) {
                    if ((<Material>ct.material).opacity > 0.01 &&
                        CameraUtils.IsVisibleSphere(frustum, ct.geometry.boundingSphere)) {
                    
                        let sphere = ct.geometry.boundingSphere;

                        // The above sphere/frustum intersection is too coarse, especially:
                        // a) using a 3D sphere instead of a circle
                        // b) for a big canvas/tile.
                        // Instead, sample the circle (sphere intersected with canvas plane) in
                        // a 10x10 grid, and test those ~95 points for visibility.
                        if (canvas.m_plane) { // All recently exported scenes will have normals
                            // Find an arbitrary vector on the tile plane, call it the "x" axis
                            var xdir : Vector3 = new Vector3(0,1,0);
                            if(canvas.m_plane.normal.angleTo(xdir) < 20 * _Math.DEG2RAD)
                                // Normal is too close to arbitrary choice of axis
                                xdir.set(1,0,0);
                            xdir.cross(canvas.m_plane.normal);
                            xdir.normalize();
                            var ydir : Vector3 = xdir.clone();
                            ydir.cross(canvas.m_plane.normal);
                            ydir.normalize(); // Shouldn't be required - as x and normal should be perpendicular.
                            const nsteps_half : number = 5;
                            // (xit,yit) are points within a circle of radius nsteps_half, sampled at integer co-ordinates
                            // (x,y) are points in a circle of radius 1
                            var pt = new Vector3;
                            for (let yit = -nsteps_half; yit <= nsteps_half; ++yit) {
                                const y : number = yit/nsteps_half;    // Normalize to -1...1
                                const xit_max : number = Math.floor(nsteps_half * Math.sqrt(Math.max(1 - y*y, 0)));
                                for (let xit = -xit_max; xit <= xit_max; ++xit)
                                {
                                    const x : number = xit/nsteps_half;   // Normalize to -1..1
                                    pt.copy(sphere.center);
                                    pt.addScaledVector(xdir, x * sphere.radius);
                                    pt.addScaledVector(ydir, y * sphere.radius);
                                    if (CameraUtils.IsVisiblePoint(frustum, pt))
                                    {
                                        cumulative.add(pt);
                                        ++NumVisibleCTs;
                                    }
                                }
                            }
                        }
                        else
                        {
                            // Older files without normals... be lazy, just use the sphere center.
                            // Note that while the *sphere* is visible in the frustum, its center
                            // might be outside frustum, or even behind the camera.
                            cumulative.add(sphere.center);
                            ++NumVisibleCTs;
                        }
                    }
                }
            }
        }
/*        else {
            for ( const auto& c : m_CanvasList )
            {
                // Compute the sum of centroids of visible strokes.
                Vec3 CentroidSum( 0.f );
                NumVisibleCTs += c->GetSumOfVisibleCentroids( &cam, CentroidSum );
                cumulative += CentroidSum;
            }
        }*/
    
        var newCenter: Vector3;
        var look: Vector3 = new Vector3;
        cam.getWorldDirection(look);
        if ( NumVisibleCTs > 0 )
        {
            // If the centroid is in front of the eye, use it.
            // Otherwise, use a point just in front of the eye. 
            var average: Vector3 = cumulative.clone().multiplyScalar(1 / NumVisibleCTs);
            var depthOfAverage: number = -(rbt.clone().inverse().multiplyVec3(average).z);
            var aLittleInFront: Vector3 = cam.position.clone().addScaledVector(look, 2.5);
            // Javascript Number.MIN_VALUE is for a double. C++ is for a float.
            const MCEpsilon: number = 1.2e-38; //Number.MIN_VALUE
            const HandEpsilon: number = MCEpsilon * 10;
            newCenter = (depthOfAverage < 10*HandEpsilon) ? aLittleInFront : average;
        } else {
            newCenter = cam.position.clone().addScaledVector(look, 2.5);
        }
    
        return newCenter;
    }   
};

//
// options: structure with a range of options for the MC scene loader.
// May throw an exception (from three.js) if unable to start.
// Return value:
// - true: started okay
// - false: attempting to start, but device age means it may not work well
function init(container: HTMLDivElement, options: MCOptions, failWarnHandler: (error: MCError) => void): MCScene
{
/*    console.log('Browser CodeName:', navigator.appCodeName);
    console.log('Browser Name:', navigator.appName);
    console.log('Browser Version:', navigator.appVersion);
    console.log('Cookies Enabled:', navigator.cookieEnabled);
    console.log('Browser Language:', navigator.language);
    console.log('Browser Online:', navigator.onLine);
    console.log('Platform:', navigator.platform);
    console.log('User-agent header:', navigator.userAgent);*/

    // On iOS, warn if the device is running pre-iOS 10.x.x (devices with < 1GB cannot upgrade to iOS 10)
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        const uaindex : number = navigator.userAgent.indexOf('OS ');
        const userOSver : string = navigator.userAgent.substr(uaindex + 3, 2).replace('_', ' ');
        if (Number(userOSver) < 10)
            failWarnHandler(new MCError ('oldDevice'));
    }
    // On Android, warn if the device is pre 6.x.x (TODO: hard-warn on 5.x.x or earlier as well?)
    else if (/Android/i.test(navigator.userAgent)) {
        const uaindex : number = navigator.userAgent.indexOf('Android ');
        const androidVer : number = parseFloat(navigator.userAgent.slice(uaindex + 8));
        if (androidVer < 6.0)
            failWarnHandler(new MCError('oldDevice'));
    }

    if (container.children.length > 0)
        console.warn('Container is not empty');

    return new MCScene(container, options, failWarnHandler);
}

/*
  What's happening here?

  This is the boundary between our ES6 code and the ES5 code that's in index.html.
  Anything that ES5 code needs to be able to call should be exposed here.

  Warning: Mental Canvas has not yet formalized its API. This will be
  formalized and significantly reduced in the near future.
 */
var DefaultExport = {
    run(container: HTMLDivElement, options: MCOptions, failWarnHandler: (error: MCError) => void): MCScene {
        return init(container, options, failWarnHandler);
    },
    parseUrl(urlSearch: string): MCOptions {
        return MCOptions.parseUrl(urlSearch);
    }
};
export default DefaultExport;
